import { CONTROL_LIT } from "@majourneys/shared/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";

/**
 * The hub's FAQ, in pewter. Each question is its own framed panel rather than
 * a glass card; the "Quick answers" badge is a plain control pill, since the
 * band never goes on something that reads as a button.
 */

const NAVY = "#110E39";
const MUTED = "#5a5d77";
/** A control at rest — the one definition, in `@majourneys/shared/ui`. */
const CONTROL_FILL = CONTROL_LIT;
const CONTROL_EDGE = "rgba(17,14,57,0.16)";

interface CategoryFaqProps {
  faqs: Array<{ q: string; a: string }>;
  title?: string;
}

export function CategoryFaq({ faqs, title }: CategoryFaqProps) {
  const { t } = useTranslation();
  const displayTitle = title ?? t("activity.faq_default_title", { defaultValue: "Frequently asked questions" });
  const [open, setOpen] = useState<number | null>(0);
  if (faqs.length === 0) return null;

  return (
    <section aria-label={t("common.faqs")} className="py-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <h2
          className="text-[22px] font-semibold leading-[1.15] sm:text-[28px]"
          style={{ color: NAVY, fontFamily: "var(--font-geist)", letterSpacing: "-0.025em" }}
        >
          {displayTitle}
        </h2>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase"
          style={{ color: MUTED, letterSpacing: "0.09em", background: CONTROL_FILL, boxShadow: `0 0 0 1.5px ${CONTROL_EDGE}` }}
        >
          <Icon name="ri-questionnaire-line" className="text-[13px] text-[color:var(--color-primary)]" aria-hidden="true" />
          {t("activity.quick_answers", { defaultValue: "Quick answers" })}
        </span>
      </div>
      <ul className="grid gap-3 lg:grid-cols-2">
        {faqs.map((f, i) => {
          const isOpen = open === i;
          // A grid of small repeating rows: one light from above, so ten bands
          // in a column read as one material rather than as glitter.
          return (
            <li key={f.q} className="h-fit" style={panelStyle(20, undefined, "topLit")}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-start"
              >
                <span className="text-[15px] font-semibold" style={{ color: NAVY }}>
                  {f.q}
                </span>
                <Icon
                  name="ri-arrow-down-s-line"
                  className={`flex-shrink-0 text-[20px] text-[#5a5d77] transition-transform ${isOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>
              {isOpen && (
                <div className="px-5 pb-5 text-[14px] leading-relaxed" style={{ color: MUTED }}>
                  {f.a}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
